// Libs
import classNames from 'classnames/bind';
import { Button, Flex, Heading, IconRefreshLine, Spinner, Text } from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
// Components, Layouts, Pages
import CourseTable from '../courseTable/CourseTable';
// Others
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { useCanvasSessionExpired } from '~/utils/hooks/canvasSession';
import { COURSE_ACTIVATION_MESSAGE_KEYS } from '~/utils/constants/common';
import { studentRoute } from '~/utils/routes/student';
import { IModule } from '~/utils/interface/course';
import { getCourseUpcoming } from '~/thunks/course/courseThunk';
// Styles, images, icons
import styles from './ScheduledLesson.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const ScheduledLesson = (props: Props) => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/home', 'common/base', 'common/course']);
  const dispatch = useAppDispatch();
  const isCanvasSessionExpired = useCanvasSessionExpired();
  //#endregion Declare Hook

  //#region Selector
  const { scheduledLessons, isLoading, errorMessage, errorCode } = useAppSelector((state) => state.courseState);
  // Course not active yet or no course: the available lessons card explains it, this one stays empty
  const hasActivationMessage = !!errorCode && !!COURSE_ACTIVATION_MESSAGE_KEYS[errorCode];
  const navigate = useNavigate();
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleClickCourseItem = (courseId?: string, moduleId?: string) => {
    navigate(`${studentRoute.base}${studentRoute.course}/${courseId}${studentRoute.module}/${moduleId}`);
  };

  const handleGetListCourses = async () => {
    await dispatch(getCourseUpcoming()).unwrap();
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={cx('centerContainer')}>
          <Spinner size='small' />
        </div>
      );
    }

    if (errorMessage && !hasActivationMessage) {
      return (
        <div className={cx('centerContainer')}>
          <Flex justifyItems='center'>
            {isCanvasSessionExpired ? (
              <Text variant='contentSmall'>{t('canvas_session_expired_card_message', { ns: 'common/base' })}</Text>
            ) : (
              <Button onClick={handleGetListCourses} size='medium' color='primary' renderIcon={<IconRefreshLine />}>
                {t('retry_button_label', { ns: 'common/base' })}
              </Button>
            )}
          </Flex>
        </div>
      );
    }

    return (
      <>
        <div className={cx('header')}>
          <Heading level='h3'>{t('study_courses_program_lesson_available_from')}</Heading>

          <Text variant='contentSmall'>
            {scheduledLessons.length > 1
              ? t('study_courses_future_plural', { ns: 'common/home', count: scheduledLessons.length })
              : t('study_courses_future', { ns: 'common/home', count: scheduledLessons.length })}
          </Text>
        </div>

        {scheduledLessons?.length > 0 ? (
          <CourseTable
            courses={scheduledLessons}
            onClick={(module: IModule) => handleClickCourseItem(module.courseId, module.id)}
          />
        ) : (
          <div className={cx('centerContainer')}>
            <Flex justifyItems='center' alignItems='center'>
              <Text variant='contentSmall'>
                {t('study_courses_program_no_scheduled_lesson', { ns: 'common/home' })}
              </Text>
            </Flex>
          </div>
        )}
      </>
    );
  };
  //#endregion Handle Function

  return <>{renderContent()}</>;
};

export default ScheduledLesson;
